import { FC, useRef } from 'react';

import dashboardMultiActionsButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_multi_actions_button_click';
import { Button, Flex, Text, TooltipHover, useBreakpoint } from '@hh.ru/magritte-ui';
import { PauseOutlinedSize24, PlusCircleOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import { useCheckedCampaignIds } from 'components/CampaignsDashboard/hooks/useCheckedCampaignIds';
import useTrls from 'hooks/translation/useTrls';

import {
    CampaignsTab,
    setCampaignIdsForBudgetIncrease,
    setIsBudgetIncreaseModalVisible,
    setStopCampaignAlert,
} from 'store/models/campaignsDashboard';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    stop: 'CampaignsMultiActionsPanel.stop',
    budgetIncrease: 'CampaignsMultiActionsPanel.budgetIncrease',
    budgetIncreaseInfoTitle: 'CampaignsMultiActionsPanel.budgetIncrease.info.title',
    budgetIncreaseInfoInfinityBudget: 'CampaignsMultiActionsPanel.budgetIncrease.info.infinityBudget',
    budgetIncreaseInfoStoppedByEndDate: 'CampaignsMultiActionsPanel.budgetIncrease.info.stoppedByEndDate',
};

export const CampaignsMultiActionsPanel: FC = () => {
    const campaigns = useSelector((state) => state.campaignsDashboard.campaigns);
    const campaignsCurrentTab = useSelector((state) => state.campaignsDashboard.campaignsCurrentTab);
    const {
        infiniteBudgetCampaignsCount,
        stoppedByEndDateCampaignsCount,
        campaignIdsForBudgetIncrease,
        campaignIdsForStop,
    } = useCheckedCampaignIds();

    const budgetIncreaseTooltipRef = useRef<HTMLDivElement>(null);

    const trls = useTrls();
    const dispatch = useDispatch();
    const { isMobile } = useBreakpoint();

    const campaignsCount = campaigns.size;
    const hasExcludedFromBudgetIncrease = infiniteBudgetCampaignsCount > 0 || stoppedByEndDateCampaignsCount > 0;

    const openMultiStopAlert = () => {
        dispatch(setStopCampaignAlert({ isVisible: true, campaignIds: campaignIdsForStop }));

        dashboardMultiActionsButtonClick({
            buttonName: 'dashboard_multi_actions_stop',
            checkedCampaignsCount: campaignIdsForStop.size,
            campaignIds: JSON.stringify([...campaignIdsForStop]),
        });
    };

    const openBudgetIncreaseModal = () => {
        dispatch([
            setCampaignIdsForBudgetIncrease(new Set(campaignIdsForBudgetIncrease)),
            setIsBudgetIncreaseModalVisible(true),
        ]);

        dashboardMultiActionsButtonClick({
            buttonName: 'dashboard_multi_actions_increase_budget',
            checkedCampaignsCount: campaignIdsForBudgetIncrease.size,
            campaignIds: JSON.stringify([...campaignIdsForBudgetIncrease]),
        });
    };

    const stopActionButton = (
        <Button
            mode="secondary"
            style="neutral"
            icon={<PauseOutlinedSize24 />}
            onClick={openMultiStopAlert}
            postfix={isMobile ? undefined : campaignIdsForStop.size}
            hideLabel={isMobile}
            aria-label={isMobile ? trls(TrlKeys.stop) : undefined}
            data-qa="campaigns-multi-actions-stop"
        >
            {trls(TrlKeys.stop)}
        </Button>
    );

    if (campaignsCount === 0 || campaignsCurrentTab === CampaignsTab.Archived) {
        return null;
    }

    return (
        <Flex
            align="center"
            gap={12}
            direction={isMobile ? 'row-reverse' : 'row'}
            justify={isMobile ? 'flex-end' : undefined}
            wrap={isMobile ? 'nowrap' : 'wrap'}
            data-qa="campaigns-multi-actions-panel"
        >
            {campaignIdsForBudgetIncrease.size > 0 && (
                <>
                    <Flex ref={budgetIncreaseTooltipRef} grow={isMobile ? 1 : undefined} minWidth={0}>
                        <Button
                            mode="secondary"
                            style="neutral"
                            icon={<PlusCircleOutlinedSize24 />}
                            onClick={openBudgetIncreaseModal}
                            postfix={isMobile ? undefined : campaignIdsForBudgetIncrease.size}
                            stretched={isMobile}
                            spaceBetween={isMobile ? false : undefined}
                            data-qa="campaigns-multi-actions-budget-increase"
                        >
                            {trls(TrlKeys.budgetIncrease, [campaignIdsForBudgetIncrease.size])}
                        </Button>
                    </Flex>
                    {hasExcludedFromBudgetIncrease && (
                        <TooltipHover activatorRef={budgetIncreaseTooltipRef} placement="bottom-center" maxWidth={320}>
                            <Text typography="paragraph-3-regular">{trls(TrlKeys.budgetIncreaseInfoTitle)}</Text>
                            {infiniteBudgetCampaignsCount > 0 && (
                                <Text typography="paragraph-3-regular">
                                    {trls(TrlKeys.budgetIncreaseInfoInfinityBudget, [infiniteBudgetCampaignsCount])}
                                </Text>
                            )}
                            {stoppedByEndDateCampaignsCount > 0 && (
                                <Text typography="paragraph-3-regular">
                                    {trls(TrlKeys.budgetIncreaseInfoStoppedByEndDate, [stoppedByEndDateCampaignsCount])}
                                </Text>
                            )}
                        </TooltipHover>
                    )}
                </>
            )}
            {campaignsCurrentTab !== CampaignsTab.Paused && campaignIdsForStop.size > 0 && stopActionButton}
        </Flex>
    );
};
